/*
 * SchoolsPage — manage the chain's school / branch list.
 *
 * SuperAdmin / SuperDev only. Lists every school (active + inactive)
 * with full CRUD plus activate / deactivate buttons. The bootstrap
 * school (id=1) cannot be hard-deleted; it can be deactivated but a
 * confirm prompt warns the operator about the consequences.
 *
 * Premium UX targets per the user brief:
 *   - Gold-cream-maroon palette matching every other admin page.
 *   - Entrance animation + hover lift on cards.
 *   - prefers-reduced-motion honoured via the CSS module.
 *   - Empty state with a clear CTA — never a blank panel.
 *   - Inactive schools render with a muted variant so the difference
 *     reads at a glance without colour reliance.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import API_BASE from "../../config";
import { extractErrorMessage } from "../../utils/apiError";
import s from "./SchoolsPage.module.css";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined,
        { year: "numeric", month: "short", day: "numeric" });
  } catch { return "—"; }
};

/** Read the logged-in user's role from session/local storage. Used to
 *  gate the hard-delete affordance — only SuperDev sees it. The backend
 *  enforces the same gate (DELETE /api/schools/{id} is SuperDev-only and
 *  refuses id=1 outright) so the UI gate is purely a UX guardrail, not
 *  a security boundary. */
const readRole = () => {
  try {
    const raw = sessionStorage.getItem("user") || localStorage.getItem("user");
    if (!raw) return null;
    const u = JSON.parse(raw);
    return u?.role || null;
  } catch { return null; }
};

export default function SchoolsPage() {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const [editing, setEditing] = useState(null); // null=closed, "new"=create, {...}=edit
  // SuperDev only — hard-delete affordance is hidden from every other
  // chain-admin role. SuperAdmin / Owner can deactivate but not delete.
  const role = useMemo(() => readRole(), []);
  const canHardDelete = role === "SuperDev";

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/api/schools/all`);
      if (!r.ok) throw new Error(`Load failed (${r.status})`);
      const list = await r.json();
      setSchools(Array.isArray(list) ? list : []);
      setError("");
    } catch (e) {
      setError(e.message || "Failed to load schools");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const counts = useMemo(() => {
    const active = schools.filter(x => x.active).length;
    return { total: schools.length, active, inactive: schools.length - active };
  }, [schools]);

  const toggleActive = async (school) => {
    const verb = school.active ? "deactivate" : "activate";
    if (school.active && !window.confirm(
        `Deactivate "${school.name}"? Existing data stays intact, but the school disappears from the topbar selector and from open-school endpoints. Reversible.`
    )) return;
    try {
      const r = await fetch(`${API_BASE}/api/schools/${school.id}/${verb}`, {
        method: "POST",
      });
      if (!r.ok) throw new Error(await extractErrorMessage(r, `Action ${verb} failed`));
      await reload();
    } catch (e) { window.alert(e.message || "Action failed"); }
  };

  /**
   * Download a per-school JSON dump — every row in every tenant
   * table filtered by school_id, plus the schools row + chain-wide
   * system tables verbatim. Useful for branch migration, compliance
   * handover, or "snapshot just this branch before the merge."
   *
   * The download flow streams the response into a Blob and uses a
   * transient anchor element to trigger the browser's save dialog —
   * same pattern used elsewhere in the codebase for receipt PDFs.
   */
  const exportBranch = async (school) => {
    try {
      const r = await fetch(`${API_BASE}/api/maintenance/export-school?schoolId=${school.id}`);
      if (!r.ok) throw new Error(await extractErrorMessage(r, `Export failed (${r.status})`));
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ncla-backup-school-${school.code || school.id}-${new Date().toISOString().slice(0,10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      window.alert(e.message || "Export failed");
    }
  };

  /**
   * Hard-delete a school. SuperDev only — both the button visibility AND
   * the backend (DELETE /api/schools/{id}) enforce the same gate.
   *
   * Requires:
   *   - Type the school's CODE literally to confirm. Prevents the
   *     muscle-memory "Enter to dismiss" mistake that a single OK/Cancel
   *     dialog allows; the operator must do something deliberate.
   *   - Strongly-worded warning that data rows are NOT removed — only
   *     the school row itself. The orphaned rows would still be there
   *     and would suddenly become unreachable. Use Deactivate if you
   *     just want to hide the branch.
   *   - Tells the operator that the bootstrap id=1 school is refused
   *     by the server (matches the production guard).
   *
   * Failure modes surfaced cleanly:
   *   - 403 → role mismatch (the session role drifted from SuperDev).
   *   - 409 → tried to delete id=1 (bootstrap school protection).
   *   - 404 → already deleted (likely a concurrent admin action).
   *   - anything else → show the server's message verbatim.
   */
  const hardDelete = async (school) => {
    if (school.id === 1) {
      window.alert(
        "The bootstrap school (id=1) cannot be deleted. Use Deactivate instead.");
      return;
    }
    const code = (school.code || "").trim();
    const typed = window.prompt(
      `⚠ Hard delete "${school.name}"?\n\n` +
      "This removes ONLY the school catalog row. Any students, employees, " +
      "invoices, payments, or other rows that referenced this branch will " +
      "be orphaned (their school_id will point at a row that no longer " +
      "exists). They won't appear anywhere in the UI again.\n\n" +
      "If you only want to HIDE this branch, click Cancel and use " +
      "Deactivate instead — it preserves the data and is reversible.\n\n" +
      `To proceed, type the school's code exactly: ${code}`
    );
    if (typed === null) return;              // user cancelled the prompt
    if (typed.trim() !== code) {
      window.alert("Code mismatch — delete cancelled.");
      return;
    }
    try {
      const r = await fetch(`${API_BASE}/api/schools/${school.id}`, {
        method: "DELETE",
      });
      if (!r.ok) {
        throw new Error(await extractErrorMessage(r, `Delete failed (${r.status})`));
      }
      await reload();
    } catch (e) {
      window.alert(e.message || "Delete failed");
    }
  };

  return (
    <div className={s.page}>
      <section className={s.hero}>
        <div className={s.heroIcon} aria-hidden>🏫</div>
        <div className={s.heroBody}>
          <div className={s.eyebrow}>Chain administration</div>
          <h1 className={s.heroTitle}>Schools &amp; branches</h1>
          <p className={s.heroSub}>
            Manage every school in the chain. Each school is a fully
            independent tenant — its students, employees, tuition,
            clinic data, and IT inventory are scoped to its
            <code>school_id</code> and never visible to staff of
            another branch.
          </p>
        </div>
      </section>

      <div className={s.kpiRow}>
        <div className={`${s.kpi} ${s.kpiAccentGold}`}>
          <div className={s.kpiLabel}>Total schools</div>
          <div className={s.kpiValue}>{counts.total}</div>
          <div className={s.kpiSub}>In the chain</div>
        </div>
        <div className={`${s.kpi} ${s.kpiAccentGreen}`}>
          <div className={s.kpiLabel}>Active</div>
          <div className={s.kpiValue}>{counts.active}</div>
          <div className={s.kpiSub}>Visible in topbar selector</div>
        </div>
        <div className={`${s.kpi} ${s.kpiAccentSlate}`}>
          <div className={s.kpiLabel}>Inactive</div>
          <div className={s.kpiValue}>{counts.inactive}</div>
          <div className={s.kpiSub}>Historical · hidden from selector</div>
        </div>
      </div>

      <div className={s.card}>
        <div className={s.cardHeader}>
          <h2 className={s.cardTitle}>All schools</h2>
          <button className={s.btnPrimary} onClick={() => setEditing("new")}>
            + Add school
          </button>
        </div>

        {error && <div className={s.errorBanner}>{error}</div>}
        {loading && <div className={s.loading}>Loading schools…</div>}

        {!loading && !error && schools.length === 0 && (
          <div className={s.emptyState}>
            <div className={s.emptyIcon}>🏫</div>
            <div className={s.emptyTitle}>No schools yet</div>
            <div className={s.emptyText}>
              The system needs at least one school. Click
              <strong> Add school </strong> to create the first one — typically
              the main campus.
            </div>
          </div>
        )}

        {!loading && schools.length > 0 && (
          <div className={s.grid}>
            {schools.map(sc => (
              <div
                key={sc.id}
                className={`${s.schoolCard} ${sc.active ? "" : s.schoolCardInactive}`}
                role="button"
                tabIndex={0}
                onClick={() => setEditing(sc)}
              >
                <div className={s.schoolCardHead}>
                  {sc.logoUrl
                    ? <img src={sc.logoUrl} alt="" className={s.schoolLogo} />
                    : <div className={s.schoolLogoDot} aria-hidden>🏫</div>}
                  <div className={s.schoolHeadText}>
                    <div className={s.schoolCode}>{sc.code}</div>
                    <div className={s.schoolName} title={sc.name}>{sc.name}</div>
                    {sc.shortName && (
                      <div className={s.schoolShort}>{sc.shortName}</div>
                    )}
                  </div>
                  <span className={sc.active ? s.pillActive : s.pillInactive}>
                    {sc.active ? "Active" : "Inactive"}
                  </span>
                </div>

                <div className={s.schoolMeta}>
                  {sc.address && <div className={s.schoolMetaRow}>📍 {sc.address}</div>}
                  {sc.phone && <div className={s.schoolMetaRow}>📞 {sc.phone}</div>}
                  {sc.email && <div className={s.schoolMetaRow}>✉ {sc.email}</div>}
                  <div className={s.schoolMetaRow}>
                    Created {fmtDate(sc.createdAt)}
                    {sc.createdByUsername && ` · by ${sc.createdByUsername}`}
                  </div>
                </div>

                <div className={s.schoolActions} onClick={e => e.stopPropagation()}>
                  <button className={s.btnGhost} onClick={() => setEditing(sc)}>Edit</button>
                  <button
                    className={s.btnGhost}
                    onClick={() => exportBranch(sc)}
                    title="Download a JSON dump of this branch's data only"
                  >
                    Export
                  </button>
                  <button
                    className={s.btnGhost}
                    onClick={() => toggleActive(sc)}
                  >
                    {sc.active ? "Deactivate" : "Activate"}
                  </button>
                  {/* Hard delete — SuperDev only, never offered on the
                       bootstrap school (id=1) so the destructive button
                       can't even be CLICKED on the row that would 409. */}
                  {canHardDelete && sc.id !== 1 && (
                    <button
                      className={s.btnDanger}
                      onClick={() => hardDelete(sc)}
                      title="Permanently remove this school row. Orphans any rows that referenced it."
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <SchoolDrawer
          school={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await reload(); }}
        />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
//  Drawer for create / edit
// ──────────────────────────────────────────────────────────────

function SchoolDrawer({ school, onClose, onSaved }) {
  const isNew = !school;
  const [form, setForm] = useState(() => ({
    code:      school?.code      || "",
    name:      school?.name      || "",
    shortName: school?.shortName || "",
    address:   school?.address   || "",
    phone:     school?.phone     || "",
    email:     school?.email     || "",
    logoUrl:   school?.logoUrl   || "",
    active:    school ? !!school.active : true,
  }));
  const [error, setError] = useState("");
  const [busy, setBusy]   = useState(false);

  useEffect(() => {
    const fn = e => { if (e.key === "Escape" && !busy) onClose(); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [onClose, busy]);

  const set = (k) => (e) => setForm(f => ({
    ...f,
    [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
  }));

  const save = async () => {
    setError("");
    if (!form.code.trim()) { setError("Code is required."); return; }
    if (!form.name.trim()) { setError("Name is required."); return; }
    setBusy(true);
    try {
      const body = {
        code:      form.code.trim().toUpperCase(),
        name:      form.name.trim(),
        shortName: form.shortName.trim() || null,
        address:   form.address.trim()   || null,
        phone:     form.phone.trim()     || null,
        email:     form.email.trim()     || null,
        logoUrl:   form.logoUrl.trim()   || null,
        active:    !!form.active,
      };
      const url = isNew
        ? `${API_BASE}/api/schools`
        : `${API_BASE}/api/schools/${school.id}`;
      const r = await fetch(url, {
        method: isNew ? "POST" : "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        throw new Error(await extractErrorMessage(r, `Save failed (${r.status})`));
      }
      await onSaved();
    } catch (e) {
      setError(e.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={s.drawerBackdrop}>
      <div className={s.drawer} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className={s.drawerHeader}>
          <h3 className={s.drawerTitle}>{isNew ? "Add school" : "Edit school"}</h3>
          <button className={s.drawerClose} onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className={s.formGrid}>
          <div className={s.formField}>
            <label className={s.formLabel}>Code *</label>
            <input className={s.formInput}
              value={form.code} onChange={set("code")}
              placeholder="MAIN, BRANCH_A, CEBU"
              disabled={!isNew && school?.id === 1}
              title={!isNew && school?.id === 1 ? "The bootstrap school's code is intentionally not editable." : ""}
            />
            <div className={s.formHint}>
              A–Z, 0–9, underscore, dash. Uppercased on save.
            </div>
          </div>
          <div className={s.formField}>
            <label className={s.formLabel}>Status</label>
            <label className={s.formCheckbox}>
              <input type="checkbox" checked={!!form.active} onChange={set("active")} />
              Active
            </label>
            <div className={s.formHint}>
              Inactive schools are hidden from the topbar selector.
            </div>
          </div>
          <div className={`${s.formField} ${s.formFieldWide}`}>
            <label className={s.formLabel}>Full name *</label>
            <input className={s.formInput}
              value={form.name} onChange={set("name")}
              placeholder="Northpoint Center for Learning and Advancement — Main Campus"
            />
          </div>
          <div className={`${s.formField} ${s.formFieldWide}`}>
            <label className={s.formLabel}>Short name</label>
            <input className={s.formInput}
              value={form.shortName} onChange={set("shortName")}
              placeholder="NCLA Main"
            />
            <div className={s.formHint}>
              Used in the topbar pill. Falls back to full name if empty.
            </div>
          </div>
          <div className={s.formField}>
            <label className={s.formLabel}>Phone</label>
            <input className={s.formInput} value={form.phone} onChange={set("phone")} />
          </div>
          <div className={s.formField}>
            <label className={s.formLabel}>Email</label>
            <input className={s.formInput} type="email" value={form.email} onChange={set("email")} />
          </div>
          <div className={`${s.formField} ${s.formFieldWide}`}>
            <label className={s.formLabel}>Address</label>
            <textarea className={s.formTextarea} value={form.address} onChange={set("address")} />
          </div>
          <div className={`${s.formField} ${s.formFieldWide}`}>
            <label className={s.formLabel}>Logo URL</label>
            <input className={s.formInput} value={form.logoUrl} onChange={set("logoUrl")} placeholder="https://…" />
            <div className={s.formHint}>
              Cloudinary URL recommended. Shown in the topbar pill + on
              every PDF letterhead.
            </div>
          </div>

          {error && <div className={s.formError}>{error}</div>}

          <div className={s.formActions}>
            <button className={s.btnGhost} onClick={onClose} disabled={busy}>Cancel</button>
            <button className={s.btnPrimary} onClick={save} disabled={busy}>
              {busy ? "Saving…" : (isNew ? "Add school" : "Save changes")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
